import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { parsePhoneNumber, isValidPhoneNumber, getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

export interface InternationalPhoneFieldProps {
    value: string;
    onChange: (value: string) => void;
    defaultCountry?: string;
    required?: boolean;
    label?: string;
    placeholder?: string;
    className?: string;
    error?: string;
}

const COUNTRIES: Array<{ code: string; name: string; iso: string }> = [
    { code: 'RW', name: 'Rwanda', iso: 'RW' },
    { code: 'KE', name: 'Kenya', iso: 'KE' },
    { code: 'UG', name: 'Uganda', iso: 'UG' },
    { code: 'TZ', name: 'Tanzania', iso: 'TZ' },
    { code: 'BI', name: 'Burundi', iso: 'BI' },
    { code: 'CD', name: 'DR Congo', iso: 'CD' },
    { code: 'GH', name: 'Ghana', iso: 'GH' },
    { code: 'NG', name: 'Nigeria', iso: 'NG' },
    { code: 'ZA', name: 'South Africa', iso: 'ZA' },
];

function getCountryFromValue(value: string): string {
    if (!value) return 'RW';
    const parsed = parsePhoneNumber(value);
    if (parsed?.country) return parsed.country;
    if (value.startsWith('+250')) return 'RW';
    if (value.startsWith('+254')) return 'KE';
    if (value.startsWith('+256')) return 'UG';
    if (value.startsWith('+255')) return 'TZ';
    if (value.startsWith('+257')) return 'BI';
    if (value.startsWith('+243')) return 'CD';
    if (value.startsWith('+233')) return 'GH';
    if (value.startsWith('+234')) return 'NG';
    if (value.startsWith('+27')) return 'ZA';
    return 'RW';
}

function formatDisplay(value: string): string {
    if (!value) return '';
    const parsed = parsePhoneNumber(value);
    if (parsed?.isValid()) {
        return parsed.formatInternational();
    }
    return value;
}

const InternationalPhoneField: React.FC<InternationalPhoneFieldProps> = ({
    value,
    onChange,
    defaultCountry = 'RW',
    required = false,
    label = 'Phone',
    placeholder,
    className = '',
    error,
}) => {
    const [selectedCountry, setSelectedCountry] = useState<string>(() =>
        value ? getCountryFromValue(value) : defaultCountry
    );
    const [nationalNumber, setNationalNumber] = useState<string>(() => {
        if (!value) return '';
        const parsed = parsePhoneNumber(value);
        if (parsed?.nationalNumber) return parsed.nationalNumber;
        const digits = value.replace(/[^\d]/g, '');
        if (digits.startsWith('250') && digits.length > 3) return digits.slice(3);
        if (digits.startsWith('0') && digits.length > 1) return digits.slice(1);
        return digits;
    });
    const [touched, setTouched] = useState(false);
    const [focused, setFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const dialCode = useMemo(() => {
        try {
            return getCountryCallingCode(selectedCountry as CountryCode);
        } catch {
            return '';
        }
    }, [selectedCountry]);

    const e164Value = useMemo(() => {
        if (!nationalNumber) return '';
        const fullNumber = `+${dialCode}${nationalNumber}`;
        try {
            const parsed = parsePhoneNumber(fullNumber);
            if (parsed?.isValid()) return parsed.number;
        } catch {
            // ignore
        }
        return fullNumber;
    }, [nationalNumber, dialCode]);

    const isValid = useMemo(() => {
        if (!e164Value) return false;
        try {
            return isValidPhoneNumber(e164Value);
        } catch {
            return false;
        }
    }, [e164Value]);

    const validationMessage = useMemo(() => {
        if (!touched || !nationalNumber) return '';
        if (!isValid) {
            const countryName = COUNTRIES.find(c => c.code === selectedCountry)?.name || selectedCountry;
            return `Enter a valid ${countryName} phone number`;
        }
        return '';
    }, [touched, nationalNumber, isValid, selectedCountry]);

    const displayError = error || validationMessage;

    const handleNationalChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^\d\s\-()]/g, '');
        setNationalNumber(raw);

        const fullNumber = `+${dialCode}${raw.replace(/[^\d]/g, '')}`;
        onChange(fullNumber);
    }, [dialCode, onChange]);

    const handleCountryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCountry = e.target.value;
        setSelectedCountry(newCountry);
        if (nationalNumber) {
            try {
                const newDialCode = getCountryCallingCode(newCountry as CountryCode);
                onChange(`+${newDialCode}${nationalNumber.replace(/[^\d]/g, '')}`);
            } catch {
                // keep current value
            }
        }
    }, [nationalNumber, onChange]);

    const handleBlur = useCallback(() => {
        setTouched(true);
        setFocused(false);
    }, []);

    const handleFocus = useCallback(() => {
        setFocused(true);
    }, []);

    useEffect(() => {
        if (value && !nationalNumber) {
            const country = getCountryFromValue(value);
            setSelectedCountry(country);
            const parsed = parsePhoneNumber(value);
            if (parsed?.nationalNumber) {
                setNationalNumber(parsed.nationalNumber);
            }
        }
    }, [value]);

    const inputId = `phone-national-${label.replace(/\s+/g, '-').toLowerCase()}`;

    return (
        <label className={`block ${className}`}>
            <span className="text-sm font-bold text-slate-700">
                {label} {required && <span className="text-red-500">*</span>}
            </span>
            <div className="mt-2 flex gap-2">
                <select
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    className="w-[140px] shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                    aria-label="Country"
                >
                    {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                            +{getCountryCallingCode(c.code as CountryCode)} {c.name}
                        </option>
                    ))}
                </select>
                <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                        +{dialCode}
                    </span>
                    <input
                        ref={inputRef}
                        id={inputId}
                        type="tel"
                        value={nationalNumber}
                        onChange={handleNationalChange}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        required={required}
                        placeholder={placeholder || (selectedCountry === 'RW' ? '788 123 456' : 'e.g. 712 345 678')}
                        aria-describedby={displayError ? `${inputId}-error` : undefined}
                        aria-invalid={Boolean(displayError)}
                        className={`mt-0 w-full rounded-xl border py-3 pl-14 pr-4 text-sm outline-none transition-all ${
                            displayError
                                ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                                : 'border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100'
                        }`}
                    />
                    {nationalNumber && touched && (
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold ${isValid ? 'text-emerald-600' : 'text-amber-500'}`}>
                            {isValid ? '✓' : '…'}
                        </span>
                    )}
                </div>
            </div>
            {displayError && (
                <p id={`${inputId}-error`} className="mt-1.5 text-xs font-semibold text-red-600" role="alert">
                    {displayError}
                </p>
            )}
            {nationalNumber && isValid && (
                <p className="mt-1.5 text-xs text-slate-400">
                    {formatDisplay(e164Value)}
                </p>
            )}
        </label>
    );
};

export default InternationalPhoneField;
